import { useId, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect, useDataItem } from 'scrivito'

import { DynamicListEmptyState } from './SubComponents/DynamicListEmptyState'
import { LIST_SIZE } from '../../const'
import { ExportCsvModal } from '../ListPersonTab/ExportCsvModal'
import { Button } from '@/Atomic/Molecules/Button'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import { SublistBody } from '@/Atomic/Organisms/RelationalSublist'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { SegmentFilterModal } from '@/Atomic/Widgets/SegmentFilterModal'
import { Company } from '@/Data/Company/CompanyDataClass'
import { CONTACT_DESC_ORDER, DataClassName } from '@/Data/const'
import { ContactListRelation } from '@/Data/ContactListRelation/ContactListRelationDataClass'
import { List } from '@/Data/List/ListDataClass'
import { Person } from '@/Data/Person/PersonDataClass'
import { useDataClassIndex } from '@/hooks/useDataClassIndex'
import { useEntitySchema } from '@/hooks/useEntitySchema'
import { invalidateViaRegistry } from '@/hooks/useInvalidationRegistry'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataItemInitials, getDataItemTitle } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getDataClassIcon } from '@/utils/image/getDataClassIcon'
import { isString } from '@/utils/isString'

import './ListContactTab.scss'

const FILTER_TOOLTIP_MAX_LENGTH = 120

interface DynamicListContactTabProps {
  contactEntity: DataClassName.COMPANY | DataClassName.PERSON
}

export const DynamicListContactTab: React.FC<DynamicListContactTabProps> =
  connect(({ contactEntity }) => {
    const { t } = useTranslation()
    const contactDataClass =
      contactEntity === DataClassName.COMPANY ? Company : Person

    const dataItem = useDataItem()
    const dataItemId = dataItem?.id() ?? ''
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)

    const { drawerItem, setDrawerItem, isDrawerOpen, openDrawer, closeDrawer } =
      useDataItemDrawer()

    const listItem = List.get(dataItemId)
    const listQuery = listItem?.get('query') as
      | Record<string, unknown>
      | undefined
    const apiFilter = listQuery?.filters as SavedFilterStructure | undefined
    const filterConditionCount =
      apiFilter?.or?.reduce(
        (count, group) => count + (group.and?.length ?? 0),
        0,
      ) ?? 0
    const hasFilter = filterConditionCount > 0

    const { attributes: schemaAttributes } = useEntitySchema(
      contactEntity.toLowerCase(),
    )

    const filterTooltipId = useId()

    const filterTooltipText = useMemo(() => {
      if (!hasFilter || !apiFilter?.or?.length) return undefined

      const seenFields = new Set<string>()
      const uniqueTitles: string[] = []
      for (const cond of apiFilter.or.flatMap((group) => group.and ?? [])) {
        if (!seenFields.has(cond.field)) {
          seenFields.add(cond.field)
          uniqueTitles.push(schemaAttributes?.[cond.field]?.title ?? cond.field)
        }
      }

      const text = uniqueTitles.join(', ')
      if (text.length <= FILTER_TOOLTIP_MAX_LENGTH) return text

      // Truncate at the last complete field name before the limit
      const truncated = text.slice(0, FILTER_TOOLTIP_MAX_LENGTH)
      const lastSeparator = truncated.lastIndexOf(', ')
      return lastSeparator > 0
        ? `${truncated.slice(0, lastSeparator)}…`
        : `${truncated}…`
    }, [hasFilter, apiFilter, schemaAttributes])

    const {
      dataItems: relationalItems,
      hasMore,
      loadMore,
      count,
    } = useDataClassIndex(
      ContactListRelation,
      {
        filters: {
          list: dataItemId,
          contactEntity,
        },
        order: [CONTACT_DESC_ORDER],
      },
      LIST_SIZE,
    )

    if (!dataItemId) return

    const sublistItems = relationalItems.flatMap((relationalItem) => {
      const contactId = relationalItem.get('contact')
      if (!isString(contactId) || !contactId.trim()) return []

      const contactDataItem = contactDataClass.get(contactId)
      if (!contactDataItem) return []

      return {
        id: relationalItem.id(),
        contactId: contactDataItem.id(),
        title: getDataItemTitle(contactDataItem) ?? '',
        subtitle: ensureString(contactDataItem.get('email')),
        image: contactDataItem.get('image') as DataBinary,
        avatarIcon: getDataClassIcon(contactEntity),
        initials: getDataItemInitials(contactDataItem),
        dataItem: contactDataItem,
      }
    })

    const handleFilterSave = () => {
      // The modal has already persisted the filter to the List (self-invalidating
      // LIST). Refresh ContactListRelation too so the connect()ed list re-queries
      // the recomputed membership without a full-page reload.
      invalidateViaRegistry(DataClassName.LIST)
    }

    const canExport = hasFilter && count > 0

    const header = (
      <div className="tab-header-wrapper">
        <Button
          icon="filter-horizontal"
          variant="outlined"
          onClick={() => setIsFilterModalOpen(true)}
          {...(filterTooltipText && {
            'data-tooltip-id': filterTooltipId,
            'aria-describedby': filterTooltipId,
          })}
        >
          {t('filter.modal.title')}
          {hasFilter &&
            t('filter.modal.activeCount', { count: filterConditionCount })}
        </Button>
        {filterTooltipText && (
          <Tooltip id={filterTooltipId} content={filterTooltipText} />
        )}
        {canExport && (
          <div className="tab-header-actions">
            <Button
              icon="download-circle"
              style={{ height: '32px' }}
              onClick={() => setIsExportModalOpen(true)}
            >
              {t('components.csv.exportTitle')}
            </Button>
          </div>
        )}
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            <div className="sublist-wrapper">
              {hasFilter ? (
                <>
                  <SublistBody
                    items={sublistItems}
                    onOpenDrawer={(item) => {
                      setDrawerItem(item.dataItem)
                      openDrawer()
                    }}
                  />
                  <SublistFooter
                    amount={sublistItems.length}
                    total={count}
                    labelKey={getEntityLabelKey(contactEntity)}
                    onLoadMore={hasMore ? loadMore : undefined}
                  />
                </>
              ) : (
                <DynamicListEmptyState
                  onCreateFilter={() => setIsFilterModalOpen(true)}
                />
              )}
            </div>
          </ScrollablePanel>
        </Sublist>
        <SegmentFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onSave={handleFilterSave}
          initialFilter={apiFilter}
          entityName={contactEntity.toLowerCase()}
          listId={dataItemId}
        />
        <ExportCsvModal
          isOpen={isExportModalOpen}
          listId={dataItemId}
          contactEntity={contactEntity}
          listTitle={dataItem?.get('title') as string}
          onClose={() => setIsExportModalOpen(false)}
        />
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
          />
        )}
      </>
    )
  })
